'use client'

import { Fragment, useEffect, useState } from 'react'
import { motion } from 'framer-motion'

import { Listing } from '@/data/getListing'
import { cn } from '@/lib/utils'
import { parseAttributes } from '@/utils/parseAttributes'
import { FavouriteIcon } from '../Favourites/FavouriteIcon'

type ListingSummaryProps = {
  listing: Listing
  address: Listing['address']
}

export const ListingSummary = ({ listing }: ListingSummaryProps) => {
  const { address, price_advertise_as, advert_brochure } = listing
  const attrs = parseAttributes(listing)
  const streetNumber = address.street_number
    ? [address.unit_number, address.street_number].filter(Boolean).join(' / ')
    : null
  const streetLine = address.formats.street_name_number || address.formats.display_address || address.street_name
  const [showAttrs, setShowAttrs] = useState(false)
  useEffect(() => {
    setTimeout(() => setShowAttrs(true))
  }, [])
  return (
    <div className="flex w-full">
      <div className="flex aspect-square w-full flex-col items-center justify-center p-10 md:aspect-auto">
        {address.hide_address !== '0' ? (
          <div className="text-center font-sans text-4xl font-semibold uppercase leading-snug tracking-widest">
            {address.suburb_or_town}
          </div>
        ) : (
          <>
            {streetNumber ? (
              <>
                <div
                  className={cn(
                    'text-center font-display text-8xl font-semibold',
                    streetNumber.length > 14 && 'text-5xl',
                  )}
                >
                  {streetNumber}
                </div>
                <div className="mt-5 text-center font-sans text-2xl font-semibold uppercase leading-snug tracking-widest">
                  {address.street_name}
                </div>
              </>
            ) : (
              <div className="text-center font-display text-5xl font-semibold leading-tight">{streetLine}</div>
            )}
            <div className="font-sans text-sm font-semibold uppercase tracking-widest">{address.suburb_or_town}</div>
          </>
        )}

        <div className="mt-6 text-center font-display text-3xl font-semibold tracking-widest">{price_advertise_as}</div>
        <div
          className={cn(
            'mt-6 min-h-[33px] w-full text-balance text-center font-body text-xs',
            !showAttrs && 'overflow-hidden safari:h-[33px] safari:opacity-0',
            attrs.length > 3 && 'safari:h-[62px]',
          )}
        >
          {attrs.map(([key, _, value, Icon]) => (
            <Fragment key={key}>
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.1 }}
                className={
                  'inline-flex h-6 w-16 flex-row items-center justify-center text-nowrap text-center transition-all'
                }
              >
                {/* Need dummy span otherwise icon-less values drop to baseline */}
                {Icon ? <Icon /> : <span className="h-6"></span>}
                <span className="ml-2 inline-flex items-center text-center text-xs tracking-widest">{value}</span>
              </motion.span>{' '}
            </Fragment>
          ))}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="inline-flex w-16 flex-row justify-center text-nowrap text-center"
          >
            <FavouriteIcon id={listing.id} className="h-6 w-6" />
          </motion.span>
        </div>
        <div className="mt-4 text-center font-body text-sm tracking-wider">{advert_brochure.heading}</div>
      </div>
    </div>
  )
}
